/** Reading a still picture: luminance, blur, and the normal-map relief Plumb shows in its "fields" panes. */

export interface Frame {
  w: number;
  h: number;
  /** Luminance, 0..1, one value per pixel. */
  lum: Float32Array;
}

const at = (v: number, max: number) => (v < 0 ? 0 : v > max ? max : v);

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Reads the image the way `object-fit: cover` shows it, at w × h. */
export function frameOf(img: HTMLImageElement, w: number, h: number, fx = 0.5, fy = 0.5): Frame {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  ctx.drawImage(img, (w - dw) * fx, (h - dh) * fy, dw, dh);
  const { data } = ctx.getImageData(0, 0, w, h);
  const lum = new Float32Array(w * h);
  for (let i = 0; i < lum.length; i++) {
    lum[i] = (data[i * 4] * 0.299 + data[i * 4 + 1] * 0.587 + data[i * 4 + 2] * 0.114) / 255;
  }
  return { w, h, lum };
}

export function blur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  if (r < 1) return src;
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  const n = r * 2 + 1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let acc = 0;
    for (let x = -r; x <= r; x++) acc += src[row + at(x, w - 1)];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc / n;
      acc += src[row + at(x + r + 1, w - 1)] - src[row + at(x - r, w - 1)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[at(y, h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / n;
      acc += tmp[at(y + r + 1, h - 1) * w + x] - tmp[at(y - r, h - 1) * w + x];
    }
  }
  return out;
}

type Rgb = [number, number, number];

const rgb = (hex: string): Rgb => {
  const n = parseInt(hex.slice(1), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
};

/** Depth as a two-tone ramp, far to near, with a contour line at every band. */
export function depthMap(frame: Frame, far: string, near: string, bands = 18): ImageData {
  const { w, h, lum } = frame;
  const image = new ImageData(w, h);
  const px = image.data;
  const a = rgb(far);
  const b = rgb(near);
  let lo = 1;
  let hi = 0;
  for (let i = 0; i < lum.length; i++) {
    if (lum[i] < lo) lo = lum[i];
    if (lum[i] > hi) hi = lum[i];
  }
  const span = Math.max(0.001, hi - lo);
  const level = (i: number) => Math.floor(((lum[i] - lo) / span) * bands);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const v = (lum[i] - lo) / span;
      const line = (x + 1 < w && level(i) !== level(i + 1)) || (y + 1 < h && level(i) !== level(i + w));
      const t = line ? 0.35 + v * 0.65 : Math.pow(v, 1.5) * 0.8;
      px[i * 4] = a[0] + (b[0] - a[0]) * t;
      px[i * 4 + 1] = a[1] + (b[1] - a[1]) * t;
      px[i * 4 + 2] = a[2] + (b[2] - a[2]) * t;
      px[i * 4 + 3] = 255;
    }
  }
  return image;
}

/** Edges of the picture as thin ink on a flat ground. */
export function edgeMap(frame: Frame, ground: string, ink: string): ImageData {
  const { w, h } = frame;
  const image = new ImageData(w, h);
  const px = image.data;
  const a = rgb(ground);
  const b = rgb(ink);
  const f = blur(frame.lum, w, h, 1);
  const p = (x: number, y: number) => f[at(y, h - 1) * w + at(x, w - 1)];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const gx = p(x + 1, y - 1) + 2 * p(x + 1, y) + p(x + 1, y + 1) - p(x - 1, y - 1) - 2 * p(x - 1, y) - p(x - 1, y + 1);
      const gy = p(x - 1, y + 1) + 2 * p(x, y + 1) + p(x + 1, y + 1) - p(x - 1, y - 1) - 2 * p(x, y - 1) - p(x + 1, y - 1);
      const m = Math.sqrt(gx * gx + gy * gy);
      const t = m < 0.09 ? 0 : Math.min(1, (m - 0.09) * 3.2);
      const i = (y * w + x) * 4;
      px[i] = a[0] + (b[0] - a[0]) * t;
      px[i + 1] = a[1] + (b[1] - a[1]) * t;
      px[i + 2] = a[2] + (b[2] - a[2]) * t;
      px[i + 3] = 255;
    }
  }
  return image;
}

/** Relief read off the picture's brightness, shown as a normal map. */
export function paint(frame: Frame, _pass: "normal"): ImageData {
  const { w, h, lum } = frame;
  const image = new ImageData(w, h);
  const px = image.data;
  const soft = blur(lum, w, h, 2);
  const terrace = new Float32Array(soft.length);
  for (let i = 0; i < soft.length; i++) terrace[i] = (Math.round(soft[i] * 12) / 12) * 0.6 + soft[i] * 0.4;
  const field = blur(terrace, w, h, 1);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const nx = -(field[y * w + at(x + 1, w - 1)] - field[y * w + at(x - 1, w - 1)]) * 16;
      const ny = -(field[at(y + 1, h - 1) * w + x] - field[at(y - 1, h - 1) * w + x]) * 16;
      const len = Math.sqrt(nx * nx + ny * ny + 1);
      px[i * 4] = (nx / len) * 127 + 128;
      px[i * 4 + 1] = (ny / len) * 127 + 128;
      px[i * 4 + 2] = (1 / len) * 127 + 128;
      px[i * 4 + 3] = 255;
    }
  }
  return image;
}
